import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { noAutomaticRateFor, rateRefusal, BAD_TYPED_RATE, typedRateValue } from "@/lib/fxPanel";
import { useSaveKey, withSaveKey } from "@/lib/saveOnce";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { useParties } from "@/lib/parties";
import { usePaymentSources, psLabel, psMeta, psOwnerName, psScopedForStudent } from "@/lib/payments";
import { Card, Btn, EmptyState, PageHeader, InfoTip } from "@/components/common";
import { useFillOnOpen } from "@/lib/fillOnOpen";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { money, fmtDate, CURRENCY_FIELD_HELP } from "@/lib/format";
import { isOverpaid, owedFigure } from "@/lib/advanceOwed";
import { curCode } from "@/lib/currencies";
import { MoneyInput } from "@/lib/accountingFormat";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, HandCoins, CheckCircle, ClockCounterClockwise, ArrowRight } from "@phosphor-icons/react";
import { ViewButton, RecordView } from "@/components/RecordView";
import { advanceFields, RECORD_TITLES } from "@/lib/recordFields";
import { Info } from "lucide-react";
import { CurrencySelect } from "@/components/CurrencySelect";
import { UnifiedDateInput } from "@/components/UnifiedDateInput";
import { NotesField } from "@/components/NotesField";
import { RecordRepaymentForm, RepaymentLine, useRepaymentAccounts, AccountPick } from "@/components/RecordRepaymentForm";
import { useDateErrors } from "@/lib/useDateErrors";
import { DeleteConfirmDialog } from "@/components/DeleteConfirmDialog";
import { AdvanceTotalsBanner, useAdvanceTotals } from "@/components/AdvanceTotalsBanner";

export default function Advances() {
  const qc = useQueryClient();
  const { selectedStudentId } = useAuth();
  const { data: students = [] } = useQuery({ queryKey: ["students"], queryFn: async () => (await api.get("/students")).data });
  const { data: parties } = useParties();
  const { data: advances = [], isLoading } = useQuery({
    queryKey: ["advances", selectedStudentId],
    queryFn: async () => (await api.get(`/advances${selectedStudentId ? `?student_id=${selectedStudentId}` : ""}`)).data,
  });
  const { data: psData } = usePaymentSources(false);
  const psSources = psData?.sources || [];
  const { data: bankAccounts = [] } = useQuery({ queryKey: ["accounts-cards"], queryFn: async () => (await api.get("/accounts-cards")).data });

  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  // The advance behind the eye — read only. (ZEN-702)
  const [viewing, setViewing] = useState(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["advances"] });
  const nameOf = (id) => students.find((s) => s.id === id)?.full_name || "—";

  const outstanding = advances.filter((a) => a.remaining > 0.005);
  const repaid = advances.filter((a) => a.remaining <= 0.005);
  // Per beneficiary and per currency, never added together (ZEN-770 piece 2).
  const { data: totals } = useAdvanceTotals();

  const [delTarget, setDelTarget] = useState(null);
  const del = async (id) => {
    try { await api.delete(`/advances/${id}`); toast.success("Deleted"); refresh(); setDelTarget(null); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <PageHeader title="Cash Advances" subtitle="Money fronted to a beneficiary that should be repaid. Track advances and record repayments."
        actions={<Btn variant="green" onClick={() => { setEditing(null); setDlg(true); }} data-testid="add-advance-btn"><Plus size={16} weight="bold" /> Add cash advance</Btn>} />

      {isLoading ? <div className="h-24 animate-pulse rounded-xl bg-white" /> : advances.length === 0 ? (
        <EmptyState icon={HandCoins} title="No cash advances yet" subtitle="Record money fronted to a beneficiary — you can then track partial or full repayments over time." />
      ) : (
        <>
          {outstanding.length > 0 && <AdvanceTotalsBanner totals={totals} nameOf={nameOf} />}
          {outstanding.length === 0 && (
            <div className="mb-4 flex items-center gap-2 rounded-lg border border-[#077A39]/30 bg-[#077A39]/5 px-4 py-3 text-sm font-semibold text-[#077A39]" data-testid="advances-all-repaid">
              <CheckCircle size={18} weight="bold" /> All advances fully repaid
            </div>
          )}

          {outstanding.length > 0 && (
            <>
              <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-[#6b7280]">Outstanding ({outstanding.length})</h2>
              <div className="mb-6 grid grid-cols-1 gap-3 lg:grid-cols-2">
                {outstanding.map((a) => (
                  <AdvanceCard key={a.id} a={a} studentName={nameOf(a.student_id)} psSources={psSources} bankAccounts={bankAccounts} students={students} parties={parties}
                    onView={() => setViewing(a)} onEdit={() => { setEditing(a); setDlg(true); }} onDelete={() => setDelTarget(a.id)} onRefresh={refresh} />
                ))}
              </div>
            </>
          )}

          {repaid.length > 0 && (
            <>
              <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-[#6b7280]">Fully repaid ({repaid.length})</h2>
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                {repaid.map((a) => (
                  <AdvanceCard key={a.id} a={a} studentName={nameOf(a.student_id)} psSources={psSources} bankAccounts={bankAccounts} students={students} parties={parties}
                    onView={() => setViewing(a)} onEdit={() => { setEditing(a); setDlg(true); }} onDelete={() => setDelTarget(a.id)} onRefresh={refresh} />
                ))}
              </div>
            </>
          )}
        </>
      )}

      <AdvanceDialog open={dlg} onClose={() => setDlg(false)} editing={editing} students={students} defaultStudent={selectedStudentId} psSources={psSources} onSaved={refresh} />
      <RecordView
        open={!!viewing}
        onClose={() => setViewing(null)}
        title={RECORD_TITLES.advance}
        fields={viewing ? advanceFields(viewing, { studentName: nameOf(viewing.student_id), students, parties, psSources, bankAccounts }) : []}
        testid="advance-view"
      />
      <DeleteConfirmDialog open={!!delTarget} onClose={() => setDelTarget(null)} onConfirm={() => del(delTarget)}
        title="Delete cash advance?" description="This will permanently delete the advance and its full repayment history." />
    </div>
  );
}

function AdvanceCard({ a, studentName, psSources, bankAccounts, students, parties, onView, onEdit, onDelete, onRefresh }) {
  const qc = useQueryClient();
  const [repayOpen, setRepayOpen] = useState(false);
  const [histOpen, setHistOpen] = useState(false);
  const isOutstanding = a.remaining > 0.005;
  const pct = a.amount > 0 ? Math.min(100, Math.round(((a.repaid_total || 0) / a.amount) * 100)) : 0;
  const isNonUSD = a.currency && a.currency !== "USD";
  const psFrom = psSources?.find((s) => s.id === a.payment_source_id);
  const bankTo = (bankAccounts || []).find((b) => b.id === a.to_payment_source_id);
  const fromLabel = psFrom ? psLabel(psFrom, psOwnerName(psFrom, students, parties)) : null;
  const toLabel = bankTo ? bankTo.label : null;

  const [undoTarget, setUndoTarget] = useState(null);
  const undoRepay = async (rid) => {
    try {
      await api.delete(`/advances/${a.id}/repay/${rid}`);
      toast.success("Repayment removed");
      setUndoTarget(null);
      qc.invalidateQueries({ queryKey: ["advances"] });
    } catch (e) { toast.error(apiErr(e)); }
  };

  const repayCur = a.repay_currency || a.currency || "USD";

  return (
    <Card data-testid={`advance-card-${a.id}`} className={isOutstanding ? "border-[#D97706]/30" : "border-[#077A39]/30"}>
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm font-bold">
            <HandCoins size={16} weight="bold" className={isOutstanding ? "text-[#D97706]" : "text-[#077A39]"} />
            {a.from_nickname || "—"} <ArrowRight size={12} weight="bold" className="text-[#6b7280]" /> {studentName}
          </div>
          <div className="mt-0.5 text-xs text-[#6b7280]">
            {fmtDate(a.date)} · {money(a.amount, a.currency)} fronted
            {isNonUSD && a.usd_amount ? ` (${money(a.usd_amount, "USD")})` : ""}
            {a.note ? ` · ${a.note}` : ""}
          </div>
          {(fromLabel || toLabel) && (
            <div className="mt-0.5 text-[11px] text-[#6b7280]" data-testid={`advance-ps-${a.id}`}>
              {fromLabel || "—"} → {toLabel || "—"}
            </div>
          )}
          {isNonUSD && a.fx_rate && (
            <div className="mt-0.5 text-[11px] text-[#6b7280]" data-testid={`advance-fx-${a.id}`}>
              1 {a.currency} = {Number(a.fx_rate).toFixed(5)} USD · {a.fx_source === "manual" ? "Manual" : a.fx_source === "treasury" ? "US Treasury" : "ECB"} · {a.fx_rate_date || ""}
            </div>
          )}
          {repayCur !== a.currency && (
            <div className="mt-0.5 text-[11px] font-semibold text-[#034AA2]" data-testid={`advance-repay-cur-${a.id}`}>
              Repayment in {repayCur}
            </div>
          )}
        </div>
        <div className="flex shrink-0 gap-0.5">
          {/* The eye, immediately before the pencil. (ZEN-702) */}
          <ViewButton onClick={onView} testid={`advance-view-${a.id}`} size={15} />
          <button onClick={onEdit} className="rounded p-1.5 text-[#034AA2] hover:bg-[#E9E9EB]" data-testid={`advance-edit-${a.id}`}><PencilSimple size={15} weight="bold" /></button>
          <button onClick={onDelete} className="rounded p-1.5 text-[#F01919] hover:bg-[#E9E9EB]" data-testid={`advance-del-${a.id}`}><Trash size={15} weight="bold" /></button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-3">
        <div className="flex items-end justify-between text-sm">
          <span className={`tnum text-lg font-extrabold ${isOutstanding ? "text-[#D97706]" : "text-[#077A39]"}`}>{isOutstanding ? `${money(a.remaining, a.currency)} owed${a.remaining_is_estimate ? " (estimate)" : ""}` : isOverpaid(a.remaining) ? owedFigure(a) : "Fully repaid"}</span>
          <span className="tnum text-xs text-[#6b7280]">{money(a.repaid_total || 0, a.currency)} of {money(a.amount, a.currency)} repaid</span>
        </div>
        <div className="mt-1 h-2 overflow-hidden rounded-full bg-[#E9E9EB]">
          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: isOutstanding ? "#D97706" : "#077A39" }} />
        </div>
      </div>

      {/* Action buttons */}
      <div className="mt-3 flex items-center gap-2">
        {isOutstanding && (<>
          <button onClick={() => setRepayOpen(!repayOpen)}
            data-testid={`advance-repay-btn-${a.id}`}
            className="inline-flex h-8 items-center gap-1 rounded-md border border-[#077A39] bg-[#077A39]/5 px-2.5 text-xs font-semibold text-[#077A39] hover:bg-[#077A39]/10">
            <CheckCircle size={14} weight="bold" /> Record repayment
          </button>
          <span className="text-[11px] text-[#6b7280]">{studentName} → {a.from_nickname || "—"}</span>
        </>)}
        {(a.repayments || []).length > 0 && (
          <button onClick={() => setHistOpen(!histOpen)} data-testid={`advance-history-btn-${a.id}`}
            className="inline-flex h-8 items-center gap-1 rounded-md px-2 text-xs font-semibold text-[#034AA2] hover:underline">
            <ClockCounterClockwise size={14} weight="bold" /> History ({(a.repayments || []).length})
          </button>
        )}
      </div>

      {/* Repayment form */}
      {repayOpen && <RecordRepaymentForm a={a} onDone={() => setRepayOpen(false)} />}

      {/* Repayment history */}
      {histOpen && (
        <div className="mt-3 rounded-md border border-[#C2C9D1] bg-[#F8FAFC] p-2.5" data-testid={`advance-history-${a.id}`}>
          <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-[#6b7280]">Repayment history</div>
          {(a.repayments || []).length === 0 ? <div className="text-xs text-[#6b7280]">No repayments yet.</div> : (
            <div className="divide-y divide-[#E9E9EB]">
              {(a.repayments || []).map((r) => (
                <div key={r.id} className="flex items-center justify-between gap-2 py-1.5 text-xs">
                  <RepaymentLine r={r} a={a} />
                  <button onClick={() => setUndoTarget(r.id)} className="rounded p-1 text-[#F01919] hover:bg-[#E9E9EB]" aria-label="delete" data-testid={`advance-undo-repay-${r.id}`}><Trash size={13} weight="bold" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <DeleteConfirmDialog open={!!undoTarget} onClose={() => setUndoTarget(null)} onConfirm={() => undoRepay(undoTarget)}
        title="Delete this repayment?" description="The repayment amount will be restored to the outstanding balance." />
    </Card>
  );
}

function AdvanceDialog({ open, onClose, editing, students, defaultStudent, psSources, onSaved }) {
  // One key for this opening, sent with every press of Save in it, so a
  // second press records nothing new. (ZEN-759; lib/saveOnce.js)
  const saveKey = useSaveKey(`${open}:${editing?.id || "new"}`);
  const nav = useNavigate();
  const { data: parties } = useParties();
  const { data: bankAccounts = [] } = useQuery({ queryKey: ["accounts-cards"], queryFn: async () => (await api.get("/accounts-cards")).data });
  const { onDateValidation, hasDateErrors } = useDateErrors();
  const [f, setF] = useState({});
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const partyOpts = [{ ref: "owner", nick: parties?.owner?.nickname || "Owner" }, ...((parties?.custom || []).map((p) => ({ ref: p.id, nick: p.nickname })))];

  // "From account" = payment sources (payer's cards/accounts)
  const fromSources = psScopedForStudent(psSources, f.student_id, f.payment_source_id);
  // "To account" = student's bank accounts only
  const toAccounts = bankAccounts.filter((b) => b.owner_type === "student" && b.student_id === f.student_id);
  const reimbAccounts = useRepaymentAccounts(f.student_id, f.reimb_from_account, f.reimb_to_account);

  const isNonUSD = f.currency && f.currency !== "USD";
  const [fxPreview, setFxPreview] = useState(null);
  const [fxLoading, setFxLoading] = useState(false);
  const [fxUnavailable, setFxUnavailable] = useState(false);
  // ZEN-770: a typed reimbursement rate, only after the server says no automatic rate exists.
  const [reimbRateNeeded, setReimbRateNeeded] = useState(false);
  const [reimbRate, setReimbRate] = useState("");
  // A1 shows only once something is typed or Save is pressed (owner, 17 Oct 2026).
  const [reimbRateTouched, setReimbRateTouched] = useState(false);
  const reimbCur = curCode(f.reimb_currency || f.repay_currency || f.currency, "USD");
  const advCur = curCode(f.currency, "USD");
  // A typed rate belongs to its own pair and date: any change clears it.
  const reimbDay = f.reimb_date || f.date;
  // Clearing the amount removes the reimbursement, so the rate box and its demand go too (round 6).
  const reimbHasAmount = parseFloat(f.reimb_amount) > 0;
  const reimbRateValue = reimbRateNeeded ? typedRateValue(reimbRate) : null;
  const badReimbRate = reimbRateNeeded && reimbRateValue === null;
  React.useEffect(() => { setReimbRateNeeded(false); setReimbRate(""); setReimbRateTouched(false); }, [advCur, reimbCur, reimbDay, reimbHasAmount]);

  useFillOnOpen(open, editing?.id || "new", () => {
    setReimbRateNeeded(false); setReimbRate(""); setReimbRateTouched(false);
    const todayStr = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; };
    setF(editing
      ? { student_id: editing.student_id, from_ref: editing.from_ref, amount: editing.amount, currency: editing.currency || "USD", date: editing.date,
          payment_source_id: editing.payment_source_id || "", to_payment_source_id: editing.to_payment_source_id || "",
          repay_currency: editing.repay_currency || editing.currency || "USD",
          fx_override: editing.fx_override || false, fx_rate: editing.fx_rate || "", note: editing.note || "",
          reimb_from: "student", reimb_date: editing.reimb_date || "", reimb_amount: editing.reimb_amount ?? "",
          reimb_currency: editing.reimb_currency || editing.repay_currency || editing.currency || "USD",
          reimb_from_account: editing.reimb_from_account || "", reimb_to_account: editing.reimb_to_account || "",
          reimb_note: editing.reimb_note || "" }
      : { student_id: defaultStudent || "", from_ref: "owner", amount: "", currency: "USD", date: todayStr(),
          payment_source_id: "", to_payment_source_id: "", repay_currency: "USD", fx_override: false, fx_rate: "", note: "",
          reimb_from: "student", reimb_date: "", reimb_amount: "", reimb_currency: "USD",
          reimb_from_account: "", reimb_to_account: "", reimb_note: "" });
    setFxPreview(null);
  });

  // Auto-fetch FX when currency/date changes
  React.useEffect(() => {
    if (!isNonUSD || f.fx_override) { setFxPreview(null); setFxUnavailable(false); return; }
    if (!f.date || f.date.length < 10) return;
    let active = true;
    setFxLoading(true);
    setFxUnavailable(false);
    api.post("/fx/preview", { date: f.date, base: f.currency, target: "USD" })
      .then((res) => { if (active) setFxPreview(res.data); })
      .catch(() => { if (active) { setFxPreview(null); setFxUnavailable(true); set("fx_override", true); } })
      .finally(() => { if (active) setFxLoading(false); });
    return () => { active = false; };
  }, [f.currency, f.date, f.fx_override, isNonUSD]); // eslint-disable-line

  const fxRate = f.fx_override ? (parseFloat(f.fx_rate) || 0) : (fxPreview?.rate || 0);
  const amt = parseFloat(f.amount) || 0;
  const usd = isNonUSD && fxRate > 0 ? amt * fxRate : amt;

  const save = async () => {
    if (badReimbRate) { setReimbRateTouched(true); return; }   // the rate box says why (round 5)
    if (hasDateErrors()) { toast.error("Fix the date errors before saving."); return; }
    if (!f.student_id) { toast.error("Select a beneficiary"); return; }
    if (!f.amount || parseFloat(f.amount) <= 0) { toast.error("Enter an amount"); return; }
    const nick = partyOpts.find((p) => p.ref === f.from_ref)?.nick || "";
    const body = {
      student_id: f.student_id, amount: parseFloat(f.amount), currency: f.currency, date: f.date,
      from_ref: f.from_ref, from_nickname: nick,
      payment_source_id: f.payment_source_id || null,
      to_payment_source_id: f.to_payment_source_id || null,
      repay_currency: f.repay_currency || f.currency,
      fx_override: f.fx_override || false,
      fx_rate: f.fx_override ? (parseFloat(f.fx_rate) || null) : null,
      note: f.note,
      reimb_date: f.reimb_date || null,
      reimb_amount: f.reimb_amount ? parseFloat(f.reimb_amount) : null,
      reimb_currency: f.reimb_currency || null,
      reimb_from_account: f.reimb_from_account || null,
      reimb_to_account: f.reimb_to_account || null,
      reimb_note: f.reimb_note || null,
    };
    if (reimbRateValue) body.reimb_fx_rate = reimbRateValue;
    try {
      if (editing) await api.put(`/advances/${editing.id}`, body);
      else await api.post("/advances", body, withSaveKey(saveKey));
      toast.success(editing ? "Updated" : "Cash advance recorded"); onSaved(); onClose();
    } catch (e) {
      // Nothing was stored, so the same save key is safe to resend with the rate.
      const d = e?.response?.status === 422 ? e.response.data?.detail : null;
      if (d?.rate_required && d.field === "reimbursement") { setReimbRateNeeded(true); setReimbRateTouched(false); }
      else toast.error(rateRefusal(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Cash advance</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <L label="Beneficiary" full><select className="inp" value={f.student_id || ""} onChange={(e) => set("student_id", e.target.value)} data-testid="advance-dlg-student"><option value="">— Select beneficiary —</option>{students.map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}</select></L>
          <L label="From"><select className="inp" value={f.from_ref || "owner"} onChange={(e) => set("from_ref", e.target.value)} data-testid="advance-dlg-from">{partyOpts.map((p) => <option key={p.ref} value={p.ref}>{p.nick}</option>)}</select></L>
          <L label="Date"><UnifiedDateInput value={f.date || ""} onChange={(v) => set("date", v)} testid="advance-dlg-date" preset="softFutureDate" onValidation={onDateValidation("advance_date")} /></L>
          <L label={<>Currency <InfoTip testid="advance-currency-info" text={CURRENCY_FIELD_HELP} /></>}><CurrencySelect value={f.currency || "USD"} onChange={(v) => { set("currency", v); set("repay_currency", v); set("fx_override", false); set("fx_rate", ""); }} testid="advance-dlg-currency" /></L>
          <L label="Amount"><MoneyInput currency={curCode(f.currency, "USD")} value={f.amount ?? ""} onChange={(v) => set("amount", v)} testid="advance-dlg-amount" /></L>

          {/* Accounts — from (payer) / to (student) */}
          <L label="From account">
            <select className="inp" value={f.payment_source_id || ""} onChange={(e) => { if (e.target.value === "__add__") { onClose(); nav("/accounts"); } else set("payment_source_id", e.target.value || null); }} data-testid="advance-dlg-payment-source">
              <option value="">— None —</option>
              {fromSources.map((s) => <option key={s.id} value={s.id}>{psLabel(s, psOwnerName(s, students, parties))}</option>)}
              <option value="__add__">＋ Add account…</option>
            </select>
          </L>
          <L label="To account">
            <select className="inp" value={f.to_payment_source_id || ""} onChange={(e) => { if (e.target.value === "__add__") { onClose(); nav("/accounts"); } else set("to_payment_source_id", e.target.value || null); }} data-testid="advance-dlg-to-account">
              <option value="">— None —</option>
              {toAccounts.map((b) => <option key={b.id} value={b.id}>{psLabel(b, psOwnerName(b, students, parties))}</option>)}
              <option value="__add__">＋ Add account…</option>
            </select>
          </L>
        </div>

        {/* FX conversion panel — non-USD only */}
        {isNonUSD && (
          <div className="rounded-lg border border-[#C2C9D1] bg-[#E9E9EB]/50 p-3" data-testid="advance-dlg-fx-panel">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Info size={16} className="text-[#034AA2]" />
                Currency conversion
              </div>
              <label className="flex items-center gap-1.5 text-xs font-medium">
                <input type="checkbox" checked={f.fx_override || false} onChange={(e) => { set("fx_override", e.target.checked); if (!e.target.checked) { set("fx_rate", ""); setFxUnavailable(false); } }} data-testid="advance-dlg-fx-override" />
                Manual override
              </label>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
              {f.fx_override ? (
                <label className="flex items-center gap-1.5">
                  <span className="text-xs font-semibold text-[#6b7280]">Rate</span>
                  <input type="number" step="0.0001" value={f.fx_rate || ""} onChange={(e) => set("fx_rate", e.target.value)} data-testid="advance-dlg-fx-rate" placeholder="e.g., 0.7213"
                    className="h-8 w-28 rounded border border-[#C2C9D1] px-2 text-sm" />
                </label>
              ) : (
                <span className="flex items-center gap-2 text-[#6b7280]">
                  {fxLoading ? <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-[#034AA2] border-t-transparent" /> : null}
                  {fxPreview ? `1 ${f.currency} = ${fxPreview.rate} USD · ${fxPreview.source === "treasury" ? "US Treasury" : "ECB"} · ${fxPreview.rate_date || ""}` : (fxLoading ? "Fetching rate…" : "Enter a date to fetch rate")}
                </span>
              )}
              {amt > 0 && fxRate > 0 && (
                <span className="ml-auto rounded-md bg-[#034AA2] px-3 py-1 font-bold text-white tnum" data-testid="advance-dlg-usd-preview">
                  = {money(usd, "USD")}
                </span>
              )}
            </div>
            {fxUnavailable && f.fx_override && (
              <div className="mt-1.5 flex items-start gap-1.5 rounded-md border border-[#D97706]/30 bg-[#FFFBEB] px-2.5 py-1.5 text-[11px] text-[#92400E]" data-testid="advance-fx-unavailable">
                {noAutomaticRateFor(f.currency)}
              </div>
            )}
          </div>
        )}

        {/* Repayment currency */}
        {isNonUSD && (
          <div className="rounded-lg border border-[#C2C9D1] bg-white p-3" data-testid="advance-dlg-repay-currency-panel">
            <span className="mb-1.5 block text-xs font-bold uppercase text-[#6b7280]">Repayment currency</span>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                <input type="radio" name="repay-cur" checked={f.repay_currency === f.currency} onChange={() => set("repay_currency", f.currency)} data-testid="advance-dlg-repay-original" />
                {f.currency} (original)
              </label>
              <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                <input type="radio" name="repay-cur" checked={f.repay_currency === "USD"} onChange={() => set("repay_currency", "USD")} data-testid="advance-dlg-repay-usd" />
                USD
              </label>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <NotesField className="col-span-2" value={f.note || ""} onChange={(v) => set("note", v)} testid="advance-dlg-note" />
        </div>

        {/* Cash advance reimbursement — settlement section */}
        <div className="mt-2 border-t border-[#C2C9D1] pt-3">
          <DialogHeader><DialogTitle>Cash advance reimbursement</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <L label="From">
              <select className="inp" value={f.reimb_from || "student"} onChange={(e) => set("reimb_from", e.target.value)} data-testid="advance-dlg-reimb-from">
                <option value="student">{students.find((s) => s.id === f.student_id)?.full_name || "Student"}</option>
              </select>
            </L>
            <L label="Date"><UnifiedDateInput value={f.reimb_date || ""} onChange={(v) => set("reimb_date", v)} testid="advance-dlg-reimb-date" preset="softFutureDate" onValidation={onDateValidation("reimb_date")} /></L>
            <L label={<>Currency <InfoTip testid="advance-reimb-currency-info" text={CURRENCY_FIELD_HELP} /></>}><CurrencySelect value={f.reimb_currency || f.repay_currency || f.currency || "USD"} onChange={(v) => set("reimb_currency", v)} testid="advance-dlg-reimb-currency" /></L>
            <L label="Amount"><MoneyInput currency={curCode(f.reimb_currency || f.repay_currency || f.currency, "USD")} value={f.reimb_amount ?? ""} onChange={(v) => set("reimb_amount", v)} testid="advance-dlg-reimb-amount" /></L>
            <L label="From account">
              <AccountPick className="inp" value={f.reimb_from_account} onChange={(v) => set("reimb_from_account", v)} options={reimbAccounts.fromAccounts} onAdd={() => { onClose(); nav("/accounts"); }} testid="advance-dlg-reimb-from-acct" />
            </L>
            <L label="To account">
              <AccountPick className="inp" value={f.reimb_to_account} onChange={(v) => set("reimb_to_account", v)} options={reimbAccounts.toAccounts} onAdd={() => { onClose(); nav("/accounts"); }} testid="advance-dlg-reimb-to-acct" />
            </L>
            {reimbRateNeeded && (
              <div className="col-span-2" data-testid="advance-dlg-reimb-rate-needed">
                <p className="mb-1 text-xs text-[#92600A]">{noAutomaticRateFor(reimbCur)}</p>
                <L label={`Exchange rate (1 ${reimbCur} = ? ${advCur})`}>
                  <input type="text" inputMode="decimal" className="inp" value={reimbRate} onChange={(e) => { setReimbRate(e.target.value); setReimbRateTouched(true); }} data-testid="advance-dlg-reimb-fx-rate" />
                </L>
                {badReimbRate && reimbRateTouched && <p className="mt-1 text-xs text-[#B91C1C]" data-testid="advance-dlg-reimb-fx-rate-bad">{BAD_TYPED_RATE}</p>}
              </div>
            )}
            <NotesField className="col-span-2" value={f.reimb_note || ""} onChange={(v) => set("reimb_note", v)} testid="advance-dlg-reimb-note" />
          </div>
        </div>

        <DialogFooter><Btn variant="ghost" onClick={onClose}>Cancel</Btn><Btn enterPrimary variant="green" onClick={save} disabled={!f.student_id} data-testid="advance-dlg-save">Save</Btn></DialogFooter>
        <style>{`.inp{height:40px;width:100%;border:1px solid #C2C9D1;border-radius:6px;padding:0 8px;font-size:14px;background:#fff}`}</style>
      </DialogContent>
    </Dialog>
  );
}

function L({ label, children, full }) { return <label className={`block ${full ? "col-span-2" : ""}`}><span className="mb-1 block text-xs font-bold uppercase text-[#6b7280]">{label}</span>{children}</label>; }
